import { z } from 'zod';
/**
 * Document-version detection (uploaded documents only).
 *
 * Copies of one document — numbered revisions, date-stamped re-exports,
 * "(1)" re-downloads, docx + pdf of the same file — form a *family*. The
 * newest member is `current`; the others are `superseded` and rank LOWER in
 * retrieval (never hidden). Detection runs in the Python ingest worker and
 * the backfill script (agents/knowledge-agent/src/document_versions.py);
 * uncertain groups go to an admin review queue and stay `standalone` (no
 * ranking change) until confirmed.
 *
 * Field names below are a contract with the retrieval ranking — chunks carry
 * `superseded` (absent = false) + `effectiveDate`, kept in sync with the
 * registry row for every chunk of the sourceFile.
 */
export const VersionStatus = z.enum(['current', 'superseded', 'standalone']);
/** auto = detector decided; confirmed = an admin decided (survives
 *  re-detection and reingest); review = uncertain, waiting for an admin. */
export const VersionConfidence = z.enum(['auto', 'confirmed', 'review']);
export const EffectiveDateSource = z.enum(['filename', 'file_modified', 'uploaded']);
/**
 * `{ring}-{16 hex}` — the ring prefix tells the admin API which registry
 * collection holds the family without a cross-collection lookup.
 */
export const FamilyId = z.string().regex(/^ring[01]-[a-f0-9]{16}$/);
/** Registry-row fields owned by version detection. Spread into
 *  DocumentRegistryEntry; all optional so pre-detection rows parse. */
export const documentVersionFields = {
    /** Normalised filename stem — candidate lookup key (indexed equality). */
    familyKey: z.string().max(300).optional(),
    familyId: FamilyId.nullable().optional(),
    versionStatus: VersionStatus.optional(),
    /** Filename of the family's current member, or null. */
    supersededBy: z.string().max(255).nullable().optional(),
    versionConfidence: VersionConfidence.optional(),
    /** e.g. "v1.5", "2025-09", "pdf". */
    versionLabel: z.string().max(40).optional(),
    /** Best estimate of when THIS version was produced (RFC3339). */
    effectiveDate: z.string().max(40).nullable().optional(),
    effectiveDateSource: EffectiveDateSource.optional(),
    /** Short human-readable reason for the decision. */
    versionReason: z.string().max(500).nullable().optional(),
    /** 0 = newest. Proposed order for review families; drives promotion when
     *  the current member is deleted. */
    versionRank: z.number().int().nonnegative().nullable().optional(),
    /** Text overlap (0..1) with the family's current / proposed-current member. */
    versionSimilarity: z.number().min(0).max(1).nullable().optional(),
    /** The file's own last-modified timestamp from internal metadata. */
    fileModifiedAt: z.string().max(40).nullable().optional(),
};
export const VersionFamilyMember = z.object({
    filename: z.string(),
    documentTitle: z.string().optional(),
    ring: z.enum(['ring0', 'ring1']),
    classification: z.string().optional(),
    versionStatus: VersionStatus.optional(),
    versionConfidence: VersionConfidence.optional(),
    supersededBy: z.string().nullable().optional(),
    versionLabel: z.string().optional(),
    effectiveDate: z.string().nullable().optional(),
    effectiveDateSource: EffectiveDateSource.optional(),
    versionReason: z.string().nullable().optional(),
    versionRank: z.number().nullable().optional(),
    versionSimilarity: z.number().nullable().optional(),
    lastIngestedAt: z.string().optional(),
    chunkCount: z.number().optional(),
});
export const VersionFamily = z.object({
    familyId: FamilyId,
    ring: z.enum(['ring0', 'ring1']),
    familyKey: z.string().optional(),
    /** 'review' when any member awaits review; else the members' confidence. */
    confidence: VersionConfidence,
    members: z.array(VersionFamilyMember),
});
export const VersionFamilyListResponse = z.object({
    families: z.array(VersionFamily),
    /** Opaque cursor for the next page, null when done. */
    nextCursor: z.string().nullable(),
});
// ---------- admin mutation bodies ------------------------------------------
/** Confirm the proposed grouping; optionally pick a different current. */
export const ConfirmFamilyBody = z
    .object({ currentFilename: z.string().min(1).max(255).optional() })
    .strict();
/** Take one member out of the family (it becomes a confirmed standalone). */
export const SplitFamilyMemberBody = z.object({ filename: z.string().min(1).max(255) }).strict();
/** Make this member current; everyone else superseded; all confirmed. */
export const SetCurrentVersionBody = z.object({ filename: z.string().min(1).max(255) }).strict();
/** Dissolve the family — every member a confirmed standalone. */
export const MarkStandaloneBody = z.object({}).strict();
//# sourceMappingURL=document-version.js.map